import { Badge } from "@/components/ui/badge";
/**
 * Project properties sheet — a right-side sheet that shows a project's
 * metadata (description, folder, last modified). Triggered from the project
 * card overflow menu (Stage 2 ships the chrome).
 */
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { WorkspaceProject } from "@/components/workspace/workspace-types";

export interface WorkspaceProjectPropertiesSheetProps {
  project: WorkspaceProject | null;
  onOpenChange: (open: boolean) => void;
}

export function WorkspaceProjectPropertiesSheet({
  project,
  onOpenChange,
}: WorkspaceProjectPropertiesSheetProps): JSX.Element {
  return (
    <Sheet open={project !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        {project ? (
          <div className="space-y-4">
            <SheetHeader>
              <SheetTitle>{project.name}</SheetTitle>
              <SheetDescription>{project.description ?? "No description."}</SheetDescription>
            </SheetHeader>
            <Separator />
            <dl className="grid grid-cols-1 gap-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Created</dt>
                <dd className="mt-1 text-foreground">
                  {new Date(project.created_at).toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                  Last modified
                </dt>
                <dd className="mt-1 text-foreground">
                  {new Date(project.updated_at).toLocaleString()}
                </dd>
              </div>
            </dl>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{project.id}</Badge>
              {project.folder_id ? (
                <Badge variant="outline">Folder: {project.folder_id}</Badge>
              ) : null}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
